<template>
  <div class="rounded-t-md">
    <!-- Chart Context Banner -->
    <v-sheet
      v-if="details.chartContext"
      :class="
        $vuetify.theme.isDark
          ? 'grey darken-3 white--text'
          : 'grey lighten-4 black--text'
      "
      class="px-3 py-2 rounded-t"
    >
      <div class="d-flex align-center text-caption">
        <v-icon x-small class="mr-2">
          {{ details.chartContext.icon || "mdi-chart-box" }}
        </v-icon>
        <span class="font-weight-medium">{{ details.chartContext.label }}</span>
      </div>
    </v-sheet>

    <!-- Header -->
    <v-card-title class="text-subtitle-1 py-2">
      <v-icon v-if="details.icon" :color="details.iconColor" class="mr-2" small>
        {{ details.icon }}
      </v-icon>
      {{ details.title }}
      <v-spacer></v-spacer>
      <v-btn icon small @click="$emit('close')">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-subtitle v-if="details.subtitle" class="text-caption py-1">
      {{ details.subtitle }}
    </v-card-subtitle>

    <!-- Target Value Badge (if applicable) -->
    <v-card-subtitle v-if="details.targetValue" class="pb-2 pt-0">
      <v-icon small left>mdi-bullseye-arrow</v-icon>
      Zu erreichender Soll-Wert:
      <span class="font-weight-medium"
        >{{ details.targetValue.value
        }}{{
          details.targetValue.unit ? " " + details.targetValue.unit : ""
        }}</span
      >
    </v-card-subtitle>

    <v-divider></v-divider>

    <!-- Scrollable Content Area -->
    <v-card-text class="pa-0" :style="contentStyle">
      <!-- Summary Section (if provided) -->
      <v-sheet v-if="details.summary" class="pa-3 mb-2">
        <div
          class="text-caption grey--text text--darken-1 mb-2 font-weight-medium text-uppercase"
        >
          Zusammenfassung
        </div>
        <v-row dense>
          <v-col
            cols="4"
            v-for="(stat, index) in details.summary"
            :key="index"
            class="text-center"
          >
            <div
              class="text-h6 font-weight-bold"
              :class="stat.color ? stat.color + '--text' : 'primary--text'"
            >
              {{ stat.value }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              {{ stat.label }}
            </div>
          </v-col>
        </v-row>
      </v-sheet>
      <v-divider v-if="details.summary"></v-divider>

      <!-- Member List (for group performance) -->
      <v-list v-if="details.type === 'memberList'" dense>
        <template v-for="(member, index) in details.members">
          <!-- Group Header -->
          <v-subheader
            v-if="member.groupHeader"
            :key="'header-' + index"
            class="text-caption font-weight-bold grey--text text--darken-2"
          >
            {{ member.groupHeader }}
          </v-subheader>

          <!-- Member Item -->
          <v-list-item v-else :key="member.user.uid" class="px-3">
            <v-list-item-avatar tile>
              <v-avatar
                size="34"
                :class="
                  $vuetify.theme.isDark
                    ? 'grey darken-3 white--text'
                    : 'grey lighten-2 black--text'
                "
                rounded
                class="font-weight-medium"
              >
                {{ member.user.firstName.charAt(0)
                }}{{ member.user.lastName.charAt(0) }}
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2">
                {{ member.user.firstName }} {{ member.user.lastName }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                <template v-if="member.summary">
                  {{ member.summary }}
                </template>
                <span v-else class="grey--text"> Keine Leistungsdaten </span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-if="details.iconColor && details.icon"
                    small
                    :color="details.iconColor"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ details.icon }}
                  </v-icon>
                </template>
                <span>{{ details.subtitle }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>

      <!-- Generic List (for other chart types) -->
      <v-list v-else-if="details.type === 'list'" dense>
        <v-list-item
          v-for="(item, index) in details.items"
          :key="index"
          class="px-3"
        >
          <v-list-item-avatar
            v-if="item.avatar"
            size="32"
            :color="item.avatarColor || 'grey lighten-2'"
          >
            <span class="text-caption">{{ item.avatar }}</span>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="text-body-2">
              {{ item.title }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.subtitle" class="text-caption">
              {{ item.subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="item.value !== undefined">
            <v-chip x-small :color="item.valueColor" label>
              {{ item.value }}
            </v-chip>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <!-- Custom Content Slot -->
      <slot name="content"></slot>
    </v-card-text>
  </div>
</template>

<script>
export default {
  name: "ChartDetailsContent",
  props: {
    details: {
      type: Object,
      required: true,
    },
    contentStyle: {
      type: String,
      default: "overflow-y: auto;",
    },
  },
};
</script>
